import { CircularProgress, Divider, Typography } from '@mui/joy';
import { Fade } from '@mui/material';
import { X } from '@phosphor-icons/react';
import * as DialogPrimitives from '@radix-ui/react-dialog';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorResponse } from 'react-router-dom';

import { replacePiiDeleted } from '~/assets/utils/replacePiiDeleted';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { ROLE_VALUES } from '~/constants/customerConstant';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import customerAuthService from '~/services/customerAuthService';
import { setViewPiiRequest } from '~/slices/piiRemovalSlice';
import { RootState } from '~/utils/store';

export interface PiiRemovalRequestButtonProps {
  refetchData: () => void;
}

const PiiRemovalRequest = ({ refetchData }: PiiRemovalRequestButtonProps) => {
  const { piiRemovalRequestData, viewPiiRequest, messages } = useSelector(
    (state: RootState) => state.piiRemoval,
  );
  const userType = localStorage.getItem('user_type');
  const { createToastNotification } = useToastNotificationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [highlightedDescriptionText, setHighlightedDescriptionText] =
    useState<string>('');

  const intl = useIntl();

  const reportData = useSelector((state: RootState) => state.reportData.report);
  const handleDenyRequest = async () => {
    setIsSubmitting(true);
    try {
      await customerAuthService.denyPiiRemovalRequest(piiRemovalRequestData.id);
      setIsSubmitting(false);
       createToastNotification(
         'PII Removal Request Denied',
          'success',
        );
      await new Promise((resolve) => setTimeout(resolve, 500));
      refetchData();
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError) {
        createToastNotification(
          axiosError.response?.data?.statusText || 'An error occurred',
          'error',
        );
      }
    }
  };

  const handleApproveRequest = async () => {
    setIsSubmitting(true);
    try {
      await customerAuthService.approvePiiRemovalRequest(
        piiRemovalRequestData.id,
      );
      setIsSubmitting(false);
      createToastNotification(
        'PII Removal Request Approved',
         'success',
       );
      await new Promise((resolve) => setTimeout(resolve, 500));
      refetchData();
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError) {
        createToastNotification(
          axiosError.response?.data?.statusText || 'An error occurred',
          'error',
        );
      }
    }
  };

  const dateToString = (date: string) => {
    if (!isNaN(new Date(date).getDate())) {
      return new Date(date).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });
    } else return '--';
  };

  const applyDescriptionHighlight = useCallback(() => {
    let removedText = '';
    let lastIndex = 0;
    const positions = piiRemovalRequestData.description_pii_removed_ranges
      ? [...piiRemovalRequestData.description_pii_removed_ranges]
      : [];

    positions.sort((a, b) => a[0] - b[0]);

    positions.forEach((range) => {
      if (!range) return;
      const [start, end] = range;
      removedText += reportData.description.substring(lastIndex, start);
      removedText += `<span style="background-color: yellow">${reportData.description.substring(
        start,
        end,
      )}</span>`;
      lastIndex = end;
    });

    removedText += reportData.description.substring(lastIndex);

    const replacedText = replacePiiDeleted(removedText);
    return replacedText;
  }, [
    piiRemovalRequestData.description_pii_removed_ranges,
    reportData.description,
  ]);

  const applyMessageHighlight = useCallback(
    (ranges: [number, number][], messageContent: string) => {
      let removedText = '';
      let lastIndex = 0;
      const positions = ranges ? [...ranges] : [];

      positions.sort((a, b) => a[0] - b[0]);

      positions.forEach((range) => {
        if (!range) return;
        const [start, end] = range;
        removedText += messageContent.substring(lastIndex, start);
        removedText += `<span style="background-color: yellow">${messageContent.substring(
          start,
          end,
        )}</span>`;
        lastIndex = end;
      });

      removedText += messageContent.substring(lastIndex);

      const replacedText = replacePiiDeleted(removedText);
      return replacedText;
    },
    [piiRemovalRequestData.description_pii_removed_ranges] //eslint-disable-line
  );

  useEffect(() => {
    setHighlightedDescriptionText(applyDescriptionHighlight());
  }, [
    piiRemovalRequestData.description_pii_removed_ranges,
    applyDescriptionHighlight,
  ]);  

  return (
    <Dialog modal={false} open={viewPiiRequest}>
      <Button
        color='secondary'
        variant='soft'
        fullWidth
        onClick={() => {
          dispatch(setViewPiiRequest(true));
        }}
      >
        {intl.formatMessage({ defaultMessage: 'View PII removal request' })}
      </Button>

      <DialogPrimitives.DialogPortal>
        <div className='fixed inset-0 overflow-hidden'>
          <div className='pointer-events-none fixed inset-y-0 right-0 flex max-w-full'>
            <div className='pointer-events-auto relative w-screen max-w-sm '>
              <div className='flex h-full flex-col shadow-xl'>
                <DialogPrimitives.Content
                  className={clsx([
                    'h-full',
                    //
                    'data-[state=open]:animate-slideLeftAndFade',
                  ])}
                >
                  <Card sx={{ height: '100%', paddingBottom: '100px' }}>
                    <div className='flex justify-between items-center'>
                      <DialogTitle>
                        {intl.formatMessage({
                          defaultMessage: 'PII removal request',
                        })}
                      </DialogTitle>
                      <DialogHeader>
                        <DialogClose asChild>
                          <Button
                            color='neutral'
                            variant='plain'
                            startDecorator={<X />}
                            onClick={() => {
                              dispatch(setViewPiiRequest(false));
                            }}
                          />
                        </DialogClose>
                      </DialogHeader>
                    </div>
                    <div className='flex-1 pt-3 flex flex-col overflow-hidden'>
                      {/* Case manager Name */}
                      <Typography level='body-xs' color='neutral'>
                        {' '}
                        {intl.formatMessage({
                          defaultMessage: 'Case Manager',
                        })}
                      </Typography>
                      <Typography level='body-md' color='neutral'>
                        {reportData.assigneeName}
                      </Typography>

                      <Divider sx={{ margin: '12px 0px' }} />
                      <div className='space-y-8 h-auto overflow-y-auto'>
                        {/* Country*/}
                        {piiRemovalRequestData?.remove_country && (
                          <div>
                            <Typography level='body-xs' color='neutral'>
                              {' '}
                              {intl.formatMessage({
                                defaultMessage: 'Country',
                              })}
                            </Typography>
                            <Typography level='body-md' color='neutral'>
                              {reportData.country.name}
                            </Typography>
                          </div>
                        )}

                        {/* Reporter*/}
                        {piiRemovalRequestData?.remove_full_name && (
                          <div>
                            <Typography level='body-xs' color='neutral'>
                              {' '}
                              {intl.formatMessage({
                                defaultMessage: 'Reporter',
                              })}
                            </Typography>
                            <Typography level='body-md' color='neutral'>
                              {reportData?.reporter}
                            </Typography>
                          </div>
                        )}

                        {/* Attachment*/}
                        {piiRemovalRequestData?.remove_files && (
                          <div>
                            {' '}
                            <Typography level='body-xs' color='neutral'>
                              {' '}
                              {intl.formatMessage({
                                defaultMessage: 'Attachment',
                              })}
                            </Typography>
                            <Typography level='body-md' color='neutral'>
                              {piiRemovalRequestData?.remove_files
                                ? 'All Attachments'
                                : ''}
                            </Typography>
                          </div>
                        )}

                        {/* Description */}
                        {piiRemovalRequestData?.description_pii_removed_ranges && (
                          <div>
                            {' '}
                            <Typography level='body-xs' color='neutral'>
                              {' '}
                              {intl.formatMessage({
                                defaultMessage: 'Description',
                              })}
                            </Typography>
                            <div
                              style={{
                                height: 'auto',
                                wordBreak: 'break-all',
                                fontSize: '14px',
                              }}
                              dangerouslySetInnerHTML={{
                                __html: highlightedDescriptionText,
                              }}
                              className={'formatted-text-viewer'}
                            />
                          </div>
                        )}

                        {/* Messages*/}
                        {piiRemovalRequestData.messages.length !== 0 && (
                          <div>
                            <Typography level='body-xs' color='neutral'>
                              {' '}
                              {intl.formatMessage({
                                defaultMessage: 'Messages',
                              })}
                            </Typography>

                            {piiRemovalRequestData.messages.map((message) => {
                              return messages
                                ?.map((msg) => {
                                  if (msg.messageId === message.id) {
                                    const ranges: [number, number][] =
                                      message.content_pii_removed_ranges?.map(
                                        (range) => [range[0], range[1]],
                                      ) || [];
                                    return (
                                      <Fade
                                        in={true}
                                        key={msg.messageId}
                                        className='mt-2'
                                      >
                                        <div className='break-words'>
                                          <Typography level='body-sm'>
                                            {msg.name !== ' '
                                              ? msg.name
                                              : intl.formatMessage({
                                                  defaultMessage: 'Anonymous',
                                                })}
                                          </Typography>

                                          <Typography level='body-xs'>
                                            {message
                                              ? dateToString(
                                                  msg.createdAt
                                                    ? msg.createdAt
                                                    : '',
                                                )
                                              : ''}
                                          </Typography>

                                          {msg.messageContent ? (
                                            <div
                                              style={{
                                                height: 'auto',
                                                wordBreak: 'break-all',
                                                fontSize: '14px',
                                              }}
                                              dangerouslySetInnerHTML={{
                                                __html: applyMessageHighlight(
                                                  ranges,
                                                  msg.messageContent,
                                                ),
                                              }}
                                              className={
                                                'formatted-text-viewer'
                                              }
                                            />
                                          ) : (
                                            ''
                                          )}
                                        </div>
                                      </Fade>
                                    );
                                  }
                                  return null;
                                })
                                .filter(Boolean); // Filter out null values
                            })}
                          </div>
                        )}
                      </div>
                      <Divider />
                      {userType === ROLE_VALUES.TENANT_ADMIN.toString() ? (
                        <div className='flex flex-row justify-between p-4'>
                          <Button
                            color='neutral'
                            variant='plain'
                            onClick={handleDenyRequest}
                            disabled={isSubmitting}
                          >
                            Deny request
                          </Button>
                          <Button
                            color='secondary'
                            variant='solid'
                            onClick={handleApproveRequest}
                            disabled={isSubmitting}
                          >
                            Approve request
                          </Button>
                        </div>
                      ) : (
                        <div className='flex flex-row items-center justify-center mt-3'>
                          <Typography
                            level='title-md'
                            sx={{ color: '#FF9900' }}
                          >
                            Pending request
                          </Typography>
                        </div>
                      )}
                    </div>

                    {isSubmitting && (
                      <div className='items-center justify-center mt-1 m-auto'>
                        <CircularProgress
                          color='neutral'
                          size='sm'
                          variant='plain'
                        />
                      </div>
                    )}
                  </Card>
                </DialogPrimitives.Content>
              </div>
            </div>
          </div>
        </div>
      </DialogPrimitives.DialogPortal>
    </Dialog>
  );
};

export default PiiRemovalRequest;
